import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  X,
  Eye,
  Download,
  FileText,
  Building2,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { documentService, Document, TipoDocumento } from "../../services/documentService";

interface DocumentsProps {
  tipo: "pmoc" | "nf" | "proposta";
  title: string;
}

const tipoConfig = {
  pmoc: { title: "PMOC", gradient: "from-indigo-500 to-purple-500" },
  nf: { title: "Notas Fiscais", gradient: "from-purple-500 to-pink-500" },
  proposta: { title: "Propostas", gradient: "from-blue-500 to-indigo-500" },
};

export function Documents({ tipo, title }: DocumentsProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTipo, setSelectedTipo] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/area-cliente");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    loadTiposDocumento();
  }, [tipo]);

  useEffect(() => {
    loadDocuments();
  }, [selectedTipo, searchTerm, dataInicio, dataFim, currentPage, tipo]);

  const loadTiposDocumento = async () => {
    try {
      const stats = await documentService.getStats();
      console.log("Stats recebidos:", stats);
      console.log("Tipo atual:", tipo);
      // Filtrar tipos baseado na seção atual
      // Verifica tanto boolean true quanto number 1 (MySQL retorna 0/1)
      const filteredStats = stats.filter((t: any) => {
        if (tipo === "pmoc") return t.isPmoc === true || t.isPmoc === 1;
        if (tipo === "nf") return t.isNf === true || t.isNf === 1;
        if (tipo === "proposta") return t.isProposta === true || t.isProposta === 1;
        return true;
      });
      console.log("Stats filtrados:", filteredStats);
      setTiposDocumento(filteredStats);
    } catch (error) {
      console.error("Erro ao carregar tipos:", error);
    }
  };


  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await documentService.list({
        tipoDocumentoId: selectedTipo || undefined,
        search: searchTerm || undefined,
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
        page: currentPage,
        limit: 20,
        isPmoc: tipo === "pmoc" ? true : undefined,
        isNf: tipo === "nf" ? true : undefined,
        isProposta: tipo === "proposta" ? true : undefined,
      });
      setDocuments(result.data);
      setTotalPages(result.pagination.totalPages);
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedTipo(null);
    setSearchTerm("");
    setDataInicio("");
    setDataFim("");
    setCurrentPage(1);
  };

  const config = tipoConfig[tipo];

  return (
    <>
      <Helmet>
        <title>{title} | Portal Arcgel</title>
      </Helmet>

      <div className="min-h-screen pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Portal
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              <span className={`bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}>
                {config.title}
              </span>
            </h1>
          </motion.div>

          {/* Quick Filters - Tipos de Documento */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap gap-3 mb-6"
          >
            <button
              onClick={() => { setSelectedTipo(null); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedTipo === null
                  ? "bg-cyan-500 text-white"
                  : "bg-slate-800/50 text-slate-300 hover:bg-slate-700/50"
              }`}
            >
              Todos
            </button>
            {tiposDocumento.map((tipo) => (
              <button
                key={tipo.id}
                onClick={() => { setSelectedTipo(tipo.id); setCurrentPage(1); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedTipo === tipo.id
                    ? "bg-cyan-500 text-white"
                    : "bg-slate-800/50 text-slate-300 hover:bg-slate-700/50"
                }`}
              >
                {tipo.nome}
                {tipo.count !== undefined && tipo.count > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-white/20 rounded-full">
                    {tipo.count}
                  </span>
                )}
              </button>
            ))}
          </motion.div>


          {/* Filter Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col md:flex-row gap-4 mb-6 p-4 rounded-xl bg-slate-800/30 border border-slate-700/50"
          >
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar documentos..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-slate-900/50 border border-slate-600 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-sm">De:</span>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => { setDataInicio(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 rounded-lg bg-slate-900/50 border border-slate-600 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors text-sm [color-scheme:dark]"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-sm">Até:</span>
                <input
                  type="date"
                  value={dataFim}
                  onChange={(e) => { setDataFim(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 rounded-lg bg-slate-900/50 border border-slate-600 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors text-sm [color-scheme:dark]"
                />
              </div>
              {(selectedTipo || searchTerm || dataInicio || dataFim) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Limpar
                </button>
              )}
            </div>
          </motion.div>

          {/* Documents Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl bg-slate-800/30 border border-slate-700/50 overflow-hidden"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Nenhum documento encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Documento</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase hidden md:table-cell">Categoria</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase hidden lg:table-cell">Unidade</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase hidden md:table-cell">Referência</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase hidden lg:table-cell">Publicado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {documents.map((doc) => (
                      <tr key={doc.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                            <span className="text-white font-medium">{doc.nome}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-slate-300 text-sm">{doc.tipoDocumento.nome}</span>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-700/50 text-xs text-slate-300">
                            <Building2 className="w-3 h-3" />
                            {doc.unidade?.nome || "Matriz"}
                          </span>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-slate-400 text-sm">
                            {new Date(doc.dataReferencia).toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}
                          </span>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <span className="text-slate-400 text-sm">
                            {new Date(doc.createdAt).toLocaleDateString("pt-BR")}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => documentService.viewFile(doc.id)}
                              className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                              title="Visualizar"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => documentService.downloadFile(doc.id, doc.arquivo)}
                              className="p-2 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 transition-colors"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}


            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-700/50">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                <span className="text-slate-400 text-sm">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Próxima
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}
